import { useMemo } from 'react';

import { ReferralTier } from '../types/';

import Spacing from 'components/spacing/Spacing';

import { AnalyticsProgressBar } from './AnalyticsProgressBar';
import { CircularProgress } from './CircularProgress';

type GamefieldProgressProps = {
  currentRewards: number;
  nextRewardAt: number;
  tiers: ReferralTier[];
  circularTitle: string;
  nextRewardName: string;
};

export const GamefieldProgress = ({
  currentRewards,
  nextRewardAt,
  tiers: inputTiers,
  circularTitle,
  nextRewardName,
}: GamefieldProgressProps) => {
  // Ensure tiers always start from 0
  const tiers = useMemo(() => {
    if (inputTiers.length === 0) return [];

    // Add initial tier with 0 if first tier doesn't start from 0
    if (inputTiers[0]?.requiredRewards > 0) {
      return [{ label: '0', requiredRewards: 0 }, ...inputTiers];
    }

    return inputTiers;
  }, [inputTiers]);

  const maxRewards = tiers[tiers.length - 1]?.requiredRewards || 100;

  const calculateEqualSegmentProgress = () => {
    if (tiers.length === 0) return 0;
    if (tiers.length === 1) return currentRewards >= tiers[0].requiredRewards ? 100 : 0;

    // Find which segment we're in
    let currentSegmentIndex = 0;
    for (let i = 0; i < tiers.length - 1; i++) {
      if (currentRewards >= tiers[i].requiredRewards && currentRewards < tiers[i + 1].requiredRewards) {
        currentSegmentIndex = i;
        break;
      }
      if (currentRewards >= tiers[i + 1].requiredRewards) {
        currentSegmentIndex = i + 1;
      }
    }

    // Calculate progress within the current segment
    const segmentWidth = 100 / (tiers.length - 1);
    const baseProgress = currentSegmentIndex * segmentWidth;

    if (currentSegmentIndex >= tiers.length - 1) {
      return 100; // Completed all tiers
    }

    const segmentStart = tiers[currentSegmentIndex].requiredRewards;
    const segmentEnd = tiers[currentSegmentIndex + 1].requiredRewards;
    const segmentRange = segmentEnd - segmentStart;
    const progressInSegment = segmentRange > 0 ? (currentRewards - segmentStart) / segmentRange : 0;

    return baseProgress + progressInSegment * segmentWidth;
  };

  const progress = calculateEqualSegmentProgress();

  return (
    <div className="tw-w-full">
      {/* Mobile - Circular Progress */}
      <div className="tw-flex tw-w-full tw-justify-center lg:tw-hidden">
        <CircularProgress
          progress={progress}
          currentRewards={currentRewards}
          totalRewards={maxRewards}
          title={circularTitle}
          nextRewardName={nextRewardName}
          tiers={tiers}
        />
      </div>

      {/* Desktop - Linear Progress */}
      <div className="tw-hidden tw-w-full lg:tw-block">
        <div className="tw-relative tw-h-[24px] tw-w-full">
          {tiers.map((tier, index) => {
            // Calculate position based on equal segments
            const position = tiers.length > 1 ? (index / (tiers.length - 1)) * 100 : 0;
            const isFirst = index === 0;
            const isLast = index === tiers.length - 1;

            // First number: left-aligned, Last number: right-aligned, Middle: centered
            let positionStyle = {};
            let alignClass = '';

            if (isFirst) {
              positionStyle = { left: '0' };
            } else if (isLast) {
              positionStyle = { right: '0' };
            } else {
              positionStyle = { left: `${position}%` };
              alignClass = '-tw-translate-x-1/2';
            }

            return (
              <span
                key={tier.label}
                className={`tw-absolute tw-font-jost tw-text-white ${alignClass}`}
                style={positionStyle}
              >
                {tier.requiredRewards}
              </span>
            );
          })}
        </div>
        <Spacing height={10} />
        <AnalyticsProgressBar progress={progress} />
        <Spacing height={10} />
        <div className="tw-flex tw-w-full tw-justify-between">
          <span className="tw-font-jost tw-text-white">Next reward: {nextRewardName}</span>
          <span className="tw-font-jost tw-text-white">Left to the milestone: {nextRewardAt - currentRewards}</span>
        </div>
      </div>
    </div>
  );
};
